import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimItemLineService } from '../../services';

// Percent <-> fraction maths leaves float noise (0.07 * 100 = 7.000000000000001).
function roundTo(value: number, decimals: number) {
  return Number(value.toFixed(decimals));
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-line-dialog',
  templateUrl: './claim-item-line.dialog.html',
  standalone: false,
})
export class ClaimItemLineDialog {
  constructor(
    private formBuilder: FormBuilder,
    private lineSvc: ClaimItemLineService,
    private toastSvc: ToastService
  ) {}

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;
  @Input() item: Claim.ClaimItem.Model | null = null;

  @Output() saved = new EventEmitter<string>();

  readonly controlsConfig = CLAIM_ITEM_CONTROLS_CONFIG;

  categories = Object.values(Claim.ClaimItem.Line.Category);
  units: Claim.ClaimItem.Line.Unit[] = Claim.ClaimItem.Line.DEFAULT_UNITS;

  line: Claim.ClaimItem.Line.Model | null = null;

  form = this.formBuilder.group({
    category: [Claim.ClaimItem.Line.Category.PART, Validators.required],
    description: ['', Validators.required],
    quantity: [null as number | null, Validators.min(0)],
    unitCode: [null as string | null],
    // Set by the claim, not picked here. USD until contracts carry their own.
    currency: [{ value: 'USD', disabled: true }],
    // Dollars here, cents on save.
    rate: [null as number | null, Validators.min(0)],
    requestedAmount: [null as number | null, [Validators.required, Validators.min(0)]],
    taxable: [false],
    // 6.5 here, 0.065 on save.
    taxRatePercent: [null as number | null, [Validators.min(0), Validators.max(100)]],
    taxInclusive: [false],
  });

  async openDialog(line: Claim.ClaimItem.Line.Model | null) {
    this.line = line;

    this.form.reset({
      category: line?.category ?? Claim.ClaimItem.Line.Category.PART,
      description: line?.description ?? '',
      quantity: line?.quantity ?? null,
      unitCode: this.unitCodeFor(line),
      currency: line?.currency ?? this.claim?.totals?.currency ?? 'USD',
      rate: line?.requestedRate != null ? line.requestedRate / 100 : null,
      requestedAmount: line?.requestedAmount != null ? line.requestedAmount / 100 : null,
      taxable: line?.taxable ?? false,
      taxRatePercent: line?.taxRate != null ? roundTo(line.taxRate * 100, 4) : null,
      taxInclusive: line?.taxInclusive ?? false,
    });

    this.dialog?.open();

    const response = await this.lineSvc.units().catch(() => null);
    // Skip if another charge opened meanwhile, and keep a unit the user already picked.
    if (response?.units.length && this.line === line) {
      this.units = withCurrentUnit(response.units, line);
      if (this.form.controls.unitCode.pristine) this.form.patchValue({ unitCode: this.unitCodeFor(line) });
    }
  }

  /** An older charge only has the unit's label, so match it to a configured unit where we can. */
  private unitCodeFor(line: Claim.ClaimItem.Line.Model | null) {
    if (!line) return null;
    if (line.unitCode) return line.unitCode;

    const label = line.unit?.trim().toLowerCase();
    return (
      this.units.find(unit => unit.label.toLowerCase() === label || unit.code.toLowerCase() === label)?.code ?? null
    );
  }

  close() {
    this.dialog?.close();
  }

  async save() {
    if (!this.claim || !this.item || this.form.invalid) return;

    const value = this.form.value;
    const body = {
      category: value.category ?? Claim.ClaimItem.Line.Category.PART,
      description: value.description ?? '',
      quantity: value.quantity ?? null,
      // Edits only send a changed unit, so one the tenant has since removed is left alone.
      unitCode:
        this.line && (value.unitCode ?? null) === (this.line.unitCode ?? null) ? undefined : value.unitCode || null,
      currency: this.form.getRawValue().currency ?? 'USD',
      requestedRate: value.rate != null ? Math.round(value.rate * 100) : null,
      requestedAmount: Math.round((value.requestedAmount ?? 0) * 100),
      taxable: !!value.taxable,
      taxRate: value.taxRatePercent != null ? roundTo(value.taxRatePercent / 100, 6) : null,
      taxInclusive: !!value.taxInclusive,
    };

    const result = this.line
      ? await this.lineSvc.update(this.claim.id, this.item.id, this.line.id, body).catch(({ error }) => {
          this.toastSvc.show({ type: 'error', title: 'Something went wrong', text: error?.message });
          return null;
        })
      : await this.lineSvc.create(this.claim.id, this.item.id, body).catch(({ error }) => {
          this.toastSvc.show({ type: 'error', title: 'Something went wrong', text: error?.message });
          return null;
        });

    if (result) {
      this.toastSvc.show({
        type: 'success',
        title: this.line ? 'Charge Updated' : 'Charge Added',
        text: this.line ? 'Successfully updated the charge.' : 'Successfully added the charge to the repair job.',
      });

      this.saved.emit(result.id);
      this.close();
    }
  }
}

/** Keeps a charge's current unit selectable even after the tenant removes it from the list. */
function withCurrentUnit(units: Claim.ClaimItem.Line.Unit[], line: Claim.ClaimItem.Line.Model | null) {
  if (!line?.unitCode || units.some(unit => unit.code === line.unitCode)) return units;
  return [...units, { code: line.unitCode, label: line.unit ?? line.unitCode }];
}
